'use client';

import React from 'react';

import { Fingerprint } from 'lucide-react';

import { Spinner } from '@kit/ui/spinner';
import { Trans } from '@kit/ui/trans';

import { retrieveBiometricCredentials } from './native-biometric-service';

/**
 * Face ID button rendered inline within the sign-in form.
 * User taps to authenticate — no auto-fire, no double-prompt issues.
 */
export function NativeFaceIdButton({
  primaryColor,
  appTextColor,
  onAuthenticated,
}: {
  primaryColor: string;
  appTextColor?: string;
  onAuthenticated: (credentials: {
    userId: string;
    refreshToken: string;
  }) => void;
}) {
  const [prompting, setPrompting] = React.useState(false);

  const attemptBiometric = React.useCallback(async () => {
    if (prompting) return;
    setPrompting(true);

    const credentials = await retrieveBiometricCredentials();
    setPrompting(false);

    if (credentials) {
      onAuthenticated(credentials);
    }
  }, [onAuthenticated, prompting]);

  const inkColor = appTextColor ?? primaryColor;

  return (
    <button
      type="button"
      onClick={attemptBiometric}
      disabled={prompting}
      className="flex min-h-[48px] w-full items-center justify-center gap-2.5 rounded-xl border py-3 text-sm font-semibold transition-opacity active:scale-[0.98] disabled:opacity-40"
      style={{
        borderColor: `${inkColor}40`,
        color: inkColor,
      }}
    >
      {prompting ? (
        <Spinner className="h-5 w-5" />
      ) : (
        <>
          <Fingerprint className="h-5 w-5" style={{ color: inkColor }} />
          <Trans i18nKey="auth:nativeFaceId" defaults="Face ID" />
        </>
      )}
    </button>
  );
}
